import {type SoundURL} from '@colonist/types'
import {AudioType} from './AudioType.ts'
import {AUDIO_FILES} from './audioFiles.ts'

export class AudioManager {
    private static context?: AudioContext
    private static buffers?: Map<AudioType, AudioBuffer>
    private static masterGain?: GainNode
    static readonly DEFAULT_VOLUME = 0.5

    static get isMuted(): boolean {
        if(this.masterGain == undefined) return true

        return this.masterGain.gain.value === 0
    }

    static async init(volume: number = this.DEFAULT_VOLUME) {
        this.context = new AudioContext()
        this.buffers = new Map()

        this.masterGain = this.context.createGain()
        this.masterGain.connect(this.context.destination)
        this.masterGain.gain.value = volume

        this.unlockAudioWithFirstUserInteraction()
        await this.loadAll()
    }

    private static async load(type: AudioType, url: SoundURL) {
        if(this.buffers === undefined || this.context === undefined) throw new Error('AudioManager not initialized')
        if(this.buffers.has(type)) return

        const response = await fetch(url)
        if(!response.ok) {
            // eslint-disable-next-line no-console
            console.error(`Failed to load sound: ${url}`)
            return
        }

        const arrayBuffer = await response.arrayBuffer()
        const buffer = await this.context.decodeAudioData(arrayBuffer)
        this.buffers.set(type, buffer)
    }

    private static async loadAll() {
        if(window['Cypress'] != undefined) return

        const audioTypes = Object.values(AudioType).filter((value) => typeof value === 'number') as AudioType[]
        const promises = audioTypes.map((type) => this.load(type, AUDIO_FILES[type]))

        await Promise.all(promises)

        this.handleAudioContextLossForIOSMobile()
    }

    static play(audio: AudioType, relativeVolume: number = 1, pitch: number = 1) {
        if(this.buffers === undefined || this.context === undefined || this.masterGain === undefined) throw new Error('AudioManager not initialized')
        if(this.isMuted) return

        const buffer = this.buffers.get(audio)
        if(!buffer) return

        void this.resumeAudioContext()

        const source = this.context.createBufferSource()
        source.buffer = buffer
        source.playbackRate.value = pitch

        const gainNode = this.context.createGain()
        gainNode.gain.value = relativeVolume
        gainNode.connect(this.masterGain)
        source.connect(gainNode)
        source.start(0)
    }

    static setMasterVolume(volume: number) {
        if(this.masterGain == undefined) throw new Error('AudioManager not initialized')

        this.masterGain.gain.value = volume
    }

    static toggleMute() {
        const volume = this.isMuted ? this.DEFAULT_VOLUME : 0
        this.setMasterVolume(volume)
    }

    private static async resumeAudioContext() {
        if(this.context == undefined) throw new Error('AudioManager not initialized')
        if(this.context.state !== 'suspended') return

        await this.context.resume()
    }

    private static unlockAudioWithFirstUserInteraction() {
        if(this.context == undefined) throw new Error('AudioManager not initialized')
        const unlockAudio = async() => {
            await this.resumeAudioContext()

            document.removeEventListener('click', unlockAudio)
        }

        document.addEventListener('click', unlockAudio, {once: true})
    }

    private static handleAudioContextLossForIOSMobile() {
        document.addEventListener('visibilitychange', () => {
            if(document.visibilityState === 'visible') {
                setTimeout(async() => {
                    await this.context?.resume()
                }, 500)
            }
        }, false)
    }
}
